import { useEffect, useState } from "react"
import styled from "styled-components"
import DefaultLayout from "../components/molecules/common/DefaultLayout"
import WordList from "../components/molecules/WordList"
import SearchInput from "../components/molecules/common/SearchInput"
import { useNavigate } from "react-router-dom"
import { getWordList, searchWord } from "../api/word"
import useDebounce from "../hooks/useDebounce"
import Text from "../components/atoms/Text"
import { tutorialPageKeys } from "../data/tutorialConfig"
import { useTutorial } from "../hooks/useTutorial"
import { tutorialImages } from "../data/tutorialImages"
import TutorialOverlay from "../components/molecules/auth/TutorialOverlay"
import { logEvent } from "../utils/eventLogger"
import { useRegisterStatus } from "../hooks/useRegisterStatus"

const SearchbarWrapper = styled.div`
  padding: 24px 20px 0 20px;;
`
const NoResult = styled.div`
  padding: 20px;
  text-align: left;
`
const Emptymessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 220px; // 임시
`

const WordDictionary = () => {
  // 아이 및 음성 등록 여부 훅
  const { registerInfo, isLoading: isStatusLoading } = useRegisterStatus();

   // 튜토리얼 훅
  const page = tutorialPageKeys.WORD_DICTIONARY;
  const { show, complete } = useTutorial(page);
  const datas = tutorialImages.WORD_DICTIONARY.steps;
  
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true);

  const [query, setQuery] = useState('');
  const debouncedQuery = useDebounce(query, 200);
  const handleQuery = (e) => {
    setQuery(e.target.value)
  };

  // 아래 useEffect와 통합하면 검색어가 입력할 때마다 로그가 호출되므로 분리
  useEffect(() => {
    logEvent("dicts_view");
  }, []);

  useEffect(() => {
    const fetchDictData = async () => {
      setIsLoading(true); // 데이터 요청 시작 시 로딩 시작
      try {
        // 검색어가 없으면 전체 리스트 렌더링
        if (!debouncedQuery) {
          const rlt = await getWordList()
          setData(rlt)
          return
        }
        // 검색어가 있으면 검색 api 호출
        const params = { searchText: debouncedQuery }
        const rlt = await searchWord(params)
        setData(rlt)
      } catch (err) {
        console.error(err)
        setData([])
      } finally {
        setIsLoading(false) // 데이터가 오면 로딩 끝
      }
    }
    fetchDictData()
  }, [debouncedQuery])

  const handleBookmark = (id) => {
    const updatedData = data.map((item) => 
      item.id === id ? {...item, isBookmark: !item.isBookmark} : item)
      setData(updatedData)
  }

  const navigate = useNavigate();
  
  const handleAddWord = () => {
    // 로딩 중일 때는 클릭 방지
    if (isStatusLoading) return;

    if (!registerInfo?.isChildRegistered) {
      alert('아이를 등록해주세요');
      // navigate('/mypage/child-register');
      return;
    }
    if (!registerInfo?.isVoiceRegistered) {
      alert('아이 음성을 등록해주세요');
      // navigate('/mypage/voice-register');
      return;
    }
    
    // 로그 기록
    logEvent("word_register_start")

    navigate('/words/new')
  }

  const handleNavigate = (id) => {
    // 로그 기록
    logEvent("dict_detail_view", {
      wordDictId: id
    })
    navigate(`/words/${id}`)
  }

  const isSearching = query.trim().length > 0;

  return (
    <DefaultLayout 
      headerType="main"
      variant="action"
      onAdd={handleAddWord}
      title="소통사전"
      hasNavBar={true}>
      <SearchbarWrapper>
        <SearchInput 
          placeholder="발화내용, 의미단어, 초성을 검색해 보세요."
          value={query}
          onChange={handleQuery}/>
      </SearchbarWrapper>
      { isLoading ? null : (
        data.length > 0 ? (
          <WordList 
            list={data} 
            onToggle={handleBookmark}
            onItemClick={handleNavigate}/>
        ) : (
          <NoResult>
            {isSearching ? 
              (<Text>검색 결과가 없습니다.</Text>
            ) : (
              <Emptymessage>
                <Text variant="headline3" weight="medium">등록한 단어가 없어요.</Text>
                <Text variant="body2" color="alternative">아이가 표현하는 단어를 등록해보세요.</Text>
              </Emptymessage>
              )
            }
          </NoResult>
        )
      )}
      {show && <TutorialOverlay datas={datas} onClose={complete}/>}
    </DefaultLayout>
  )
}

export default WordDictionary
